import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import { Option } from '@/shared/components/ui/SearchableSelect';
import { apiRoutes } from "@/shared/api/routes";
import { useUserScope } from '@/shared/hooks/useUserScope';

interface ServiceAreasApiResponse {
  data: (string | number | boolean | null)[][];
  info: {
    limit: number;
    results: number;
    total_results: number;
    elapsed_total: number;
    elapsed_per_item: number;
    db_query: string;
    select_query: boolean;
  };
}

export const useServiceAreasWithPagination = (enabled: boolean = true) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Resolve allowed provider IDs from the central user scope hook.
  // Service areas are linked to providers via the `enclosure.id` field
  // (enclosure = the company/provider that "owns" the service area).
  // When non-empty, the Service Area dropdown is automatically scoped to
  // areas belonging to those providers — matching the pattern in
  // useProvidersWithPagination, useRunsWithPagination, and useVehiclesWithPagination.
  const { allowedProviderIds, isProviderRestricted } = useUserScope();

  // Stable cache key for the provider scope so restricted users don't share
  // cache entries with unrestricted users (same pattern as sibling hooks).
  const allowedIdsKey = useMemo(
    () =>
      isProviderRestricted
        ? [...allowedProviderIds].sort((a, b) => a - b).join(',')
        : '',
    [allowedProviderIds, isProviderRestricted],
  );

  // Fetch service areas with pagination and search
  const {
    data: serviceAreasData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['serviceAreas', 'pagination', searchTerm, allowedIdsKey],
    queryFn: async ({ pageParam = 0 }) => {
      const baseQuery: unknown[][] = [];
      // When provider-restricted, scope to service areas belonging to allowed
      // providers. A service area is relevant if it's directly assigned to an
      // allowed provider, or if it has a run belonging to an allowed provider.
      // Uses [-1] sentinel when the allowed list is somehow empty to return
      // zero rows rather than everything.
      if (isProviderRestricted) {
        const ids = allowedProviderIds.length > 0 ? allowedProviderIds : [-1];
        baseQuery.push([
          'or',
          ['in', 'companies.id', ids],
          ['in', 'runs.company.id', ids]
        ]);
      }
      const query = searchTerm
        ? [...baseQuery, ['like', 'enclosure.name', `%${searchTerm}%`]]
        : baseQuery;
      
      const offset = pageParam * 10;
      
      const requestBody = {
        select: ['id', 'name', 'enclosure.name'],
        query,
        options: {
          timeout: 10,
          offset,
          limit: 10,
          order: [['enclosure.name', 'asc_nulls_last']],
          calculate_total: true,
          return_results: true,
          select_distinct: true,
        },
      };

      const response = await apiClient.post<ServiceAreasApiResponse>(
        apiRoutes.proxy('support/internal/service_area/select_query'),
        requestBody
      );

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedItems = allPages.reduce((sum, page) => sum + (page.data?.length || 0), 0);
      return loadedItems < (lastPage.info?.total_results || 0) ? allPages.length : undefined;
    },
    enabled,
    initialPageParam: 0,
  });

  // Transform service areas for SearchableSelect
  const serviceAreaOptions = useMemo<Option[]>(() => {
    if (!serviceAreasData?.pages) return [];
    return serviceAreasData.pages.flatMap((page) =>
      (page.data || []).map((area: (string | number | boolean | null)[]) => ({
        id: area[0]?.toString() || '',
        name: String(area[2] || area[1] || 'Unknown Service Area'), // enclosure.name, fallback to name
      }))
    );
  }, [serviceAreasData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
  }, []);

  return {
    serviceAreaOptions,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    handleSearchChange,
  };
};